// Atlas rasterizer (ATL-3/4). PURE + deterministic (GEN-1): the Atlas generator
// implements the same chunk-pull `generate(cx,cz)` the overworld/nether use, so
// the same city renders identically on the main thread, the gen worker, and the
// server. Per chunk it lays a flat ground plane, strokes roads (+ sidewalks),
// fills water/parks, scatters Poisson trees (ATL-4), and stamps buildings (ATL-5).
import { WORLD } from '../config';
import { Chunk } from '../engine/chunk';
import { STATE_AIR, DEFAULT_RANGE, type YRange } from '../engine/section';
import { lightChunk } from '../engine/light';
import {
  pointInPolygonWithHoles, distToSegmentSq, boundsOf, bboxIntersects, ROAD_WIDTH_M,
  type IntermediateCity, type BBox, type Pt,
} from './intermediate';
import { treePoints, treeShapeRng } from './vegetation';
import { buildBuildingPlan, stampBuilding, type BuildingIds, type BuildingPlan } from './buildings';
import { ElevationField, type AtlasElevation } from './elevation';
import { AerowayField, type AerowayIds } from './aeroway';

/** Flat ground plane Y for Atlas worlds with NO elevation field (v1.0/v1.1 parity:
 *  a no-DEM Atlas world stays byte-identical). Relief worlds use `ElevationField`
 *  with the datum at `ELV.BASE_Y` (M30 / ELV-2). */
export const ATLAS_GROUND_Y = WORLD.SEA_LEVEL; // 63

/** Block-state ids the Atlas rasterizer places. */
export interface AtlasIds extends BuildingIds {
  grass: number;
  dirt: number;
  stone: number;
  bedrock: number;
  water: number;
  road: number;     // gray_concrete
  sidewalk: number; // andesite
  log: number;      // oak_log[axis=y]
  leaves: number;   // oak_leaves
  runway: number;      // light_gray_concrete (AVI-1)
  runwayPaint: number; // white_concrete — centerline + threshold
  taxiPaint: number;   // yellow_concrete — taxiway centerline
}

interface RoadSeg { ax: number; az: number; bx: number; bz: number; half: number; bb: BBox; }

/** Parameter-surface widening for the Earth reuse (ERT-2/4). All optional; absent ⇒
 *  Atlas behaviour byte-identical (default −64..319 range, no bounded ocean). */
export interface AtlasGenOpts {
  /** Vertical extent to build (TAL-1). Default ⇒ the −64..319 / 24-section column. */
  range?: YRange;
  /** Bounded-extent ocean (ERT-6): columns whose |x| or |z| exceeds `halfBlocks` are
   *  a flat sea-level ocean (a shallow shell — bedrock/seabed/water to `seaY`),
   *  killing beyond-extent tile misses. Absent ⇒ no ocean (Atlas). */
  oceanBeyond?: { halfBlocks: number; seaY: number; floorDepth: number };
  /** Stone-crust depth below the surface (Earth): the column fills bedrock→stone from
   *  `max(range.minY, ground − crustDepth)`, not the full range floor, so a deep Earth
   *  world costs like a normal one. Absent ⇒ fill to `range.minY` (Atlas byte-identical). */
  crustDepth?: number;
  /** Baked-imagery surface repaint (OVL-4, M43): given a natural-ground column and its
   *  default surface state, returns the state to place — the nearest palette block under
   *  the aligned imagery, or the default where there's no coverage. Applies ONLY to
   *  natural ground (roads/water/buildings/aeroway keep their vector state). Absent ⇒
   *  no repaint (Atlas/no-imagery Earth byte-identical). */
  surfaceOverride?: (wx: number, wz: number, top: number) => number;
}

export class AtlasGenerator {
  private readonly opacityOf: (s: number) => number;
  private readonly emissionOf: (s: number) => number;
  private readonly isBlocking: (s: number) => boolean;
  private readonly segs: RoadSeg[] = [];
  private readonly plans: BuildingPlan[] = [];
  private readonly trees: Pt[];
  /** Real relief (M30); null → flat `ATLAS_GROUND_Y` (parity). */
  private readonly elev: ElevationField | null;
  /** Per-water-polygon surface Y (its own level, not a global sea, ELV-3). */
  private readonly waterLevels: number[] = [];
  /** Runways / taxiways / aprons (AVI-1); null when the city has none (parity). */
  private readonly aeroway: AerowayField | null;
  private readonly aeroIds: AerowayIds;
  /** Vertical extent this generator builds (TAL-1); default range for Atlas. */
  private readonly range: YRange;
  /** Bounded-extent ocean (ERT-6); null for Atlas. */
  private readonly ocean: { half: number; seaY: number; floorY: number } | null;
  /** Stone-crust depth below the surface (Earth); null ⇒ fill to the range floor. */
  private readonly crustDepth: number | null;
  /** Baked-imagery natural-ground repaint (OVL-4, M43); null ⇒ no repaint (parity). */
  private readonly surfaceOverride: ((wx: number, wz: number, top: number) => number) | null;

  constructor(
    private readonly city: IntermediateCity,
    private readonly ids: AtlasIds,
    seed: number | string | bigint,
    opacityByState?: Uint8Array,
    emissionByState?: Uint8Array,
    elevation?: AtlasElevation,
    opts: AtlasGenOpts = {},
  ) {
    const op = opacityByState, em = emissionByState;
    this.opacityOf = op ? (s) => op[s] ?? 15 : (s) => (s === STATE_AIR ? 0 : 15);
    this.emissionOf = em ? (s) => em[s] ?? 0 : () => 0;
    this.isBlocking = (s) => s !== STATE_AIR && s !== ids.water;
    this.range = opts.range ?? DEFAULT_RANGE;
    this.ocean = opts.oceanBeyond
      ? { half: opts.oceanBeyond.halfBlocks, seaY: opts.oceanBeyond.seaY, floorY: opts.oceanBeyond.seaY - opts.oceanBeyond.floorDepth }
      : null;
    this.crustDepth = opts.crustDepth ?? null;
    this.surfaceOverride = opts.surfaceOverride ?? null;
    // Datum variant (ERT-2): Earth passes a sea-level datum + its own clamp range;
    // Atlas passes none → the ELV-2 min-elev datum, byte-identical.
    this.elev = elevation ? new ElevationField(elevation.dem, elevation.center, elevation.scale, elevation.datum) : null;

    // Road segments with per-class half-width (blocks) + bbox for chunk filtering.
    for (const r of city.roads) {
      const half = Math.max(1, Math.round(ROAD_WIDTH_M[r.roadClass] / city.scale / 2));
      for (let i = 0; i + 1 < r.points.length; i++) {
        const [ax, az] = r.points[i]!, [bx, bz] = r.points[i + 1]!;
        this.segs.push({ ax, az, bx, bz, half, bb: boundsOf([[ax, az], [bx, bz]]) });
      }
    }
    // Precompute building plans (door faces the nearest road) + the foundation Y =
    // min ground over the footprint (ELV-4). Flat worlds keep `ATLAS_GROUND_Y`.
    for (const b of city.buildings) {
      const bb = boundsOf(b.footprint);
      const cx = (bb.minX + bb.maxX) / 2, cz = (bb.minZ + bb.maxZ) / 2;
      const plan = buildBuildingPlan(b, ids, this.nearestRoadPoint(cx, cz));
      if (plan) { plan.floorY = this.minGroundOver(plan); this.plans.push(plan); }
    }
    // A lake sits at one flat level = its centroid's ground height (ELV-3).
    for (const poly of city.water) {
      const c = boundsOf(poly.points);
      this.waterLevels.push(this.groundYAt((c.minX + c.maxX) / 2, (c.minZ + c.maxZ) / 2));
    }
    this.trees = treePoints(city, seed);
    // Aeroways (AVI-1): built after relief so runways flatten to real mean ground.
    this.aeroIds = { runway: ids.runway, paint: ids.runwayPaint, taxiway: ids.road, taxiPaint: ids.taxiPaint, apron: ids.road };
    const af = new AerowayField(city.aeroways, city.scale, (wx, wz) => this.groundYAt(wx, wz));
    this.aeroway = af.empty ? null : af;
  }

  /** Ground block-Y at a world column (relief, ELV-3; flat worlds → 63). */
  private groundYAt(wx: number, wz: number): number {
    return this.elev ? this.elev.groundY(wx, wz) : ATLAS_GROUND_Y;
  }

  /** Lowest ground over a building footprint — its flat foundation Y (ELV-4). */
  private minGroundOver(plan: BuildingPlan): number {
    if (!this.elev) return ATLAS_GROUND_Y;
    let min = Infinity;
    for (const c of plan.cells) {
      const [x, z] = c.split(',').map(Number) as [number, number];
      const g = this.groundYAt(x, z);
      if (g < min) min = g;
    }
    return Number.isFinite(min) ? min : ATLAS_GROUND_Y;
  }

  private nearestRoadPoint(x: number, z: number): Pt | null {
    let best: Pt | null = null, bestD = Infinity;
    for (const s of this.segs) {
      const dx = s.bx - s.ax, dz = s.bz - s.az, len2 = dx * dx + dz * dz;
      const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((x - s.ax) * dx + (z - s.az) * dz) / len2));
      const px = s.ax + t * dx, pz = s.az + t * dz;
      const d = (x - px) ** 2 + (z - pz) ** 2;
      if (d < bestD) { bestD = d; best = [px, pz]; }
    }
    return best;
  }

  /** Surface classification at a world column: 0 grass, 1 road, 2 sidewalk, or
   *  `3 + waterPolygonIndex` for water (so the column can read that lake's level). */
  private classify(wx: number, wz: number, near: RoadSeg[]): number {
    const cx = wx + 0.5, cz = wz + 0.5;
    let onSidewalk = false;
    for (const s of near) {
      const d = distToSegmentSq(cx, cz, s.ax, s.az, s.bx, s.bz);
      if (d <= s.half * s.half) return 1;                    // road surface
      if (d <= (s.half + 1) * (s.half + 1)) onSidewalk = true; // 1-block sidewalk ring
    }
    if (onSidewalk) return 2;
    for (let i = 0; i < this.city.water.length; i++) if (pointInPolygonWithHoles(cx, cz, this.city.water[i]!)) return 3 + i;
    return 0;
  }

  /** A flat sea-level ocean column beyond the bounded extent (ERT-6): a shallow shell
   *  (bedrock → seabed → water to sea level) so it costs ~2 sections, not a full
   *  tall-column stone fill. Air below the seabed = the documented void divergence. */
  private oceanColumn(lx: number, lz: number, set: (lx: number, y: number, lz: number, s: number) => void): void {
    const o = this.ocean!;
    const floor = Math.max(this.range.minY, o.floorY);
    for (let y = floor; y <= o.seaY; y++) {
      set(lx, y, lz, y === floor ? this.ids.bedrock : y < o.seaY - 1 ? this.ids.stone : y < o.seaY ? this.ids.dirt : this.ids.water);
    }
  }

  generate(cx: number, cz: number): Chunk {
    const chunk = new Chunk(cx, cz, this.range);
    const set = (lx: number, y: number, lz: number, state: number): void => chunk.setBlockState(lx, y, lz, state, this.isBlocking);
    const bx = cx * WORLD.SECTION, bz = cz * WORLD.SECTION;
    // Padded chunk bbox: tree canopies + building overhangs can straddle borders.
    const pad: BBox = { minX: bx - 8, minZ: bz - 8, maxX: bx + WORLD.SECTION + 7, maxZ: bz + WORLD.SECTION + 7 };
    const near = this.segs.filter((s) => bboxIntersects({ minX: s.bb.minX - s.half - 1, minZ: s.bb.minZ - s.half - 1, maxX: s.bb.maxX + s.half + 1, maxZ: s.bb.maxZ + s.half + 1 }, pad));

    const ids = this.ids;
    // Aeroways touching this chunk (AVI-1): runways pin their column to a flat Y and
    // paint concrete, overriding road/water/grass. Off in non-aviation worlds (parity).
    const aeroField = this.aeroway && this.aeroway.intersects(pad) ? this.aeroway : null;
    const minY = this.range.minY;
    for (let lz = 0; lz < WORLD.SECTION; lz++) {
      for (let lx = 0; lx < WORLD.SECTION; lx++) {
        const wx = bx + lx, wz = bz + lz;
        // Beyond the bounded extent → flat sea-level ocean (ERT-6). Inert for Atlas.
        if (this.ocean && (Math.abs(wx) > this.ocean.half || Math.abs(wz) > this.ocean.half)) {
          this.oceanColumn(lx, lz, set);
          continue;
        }
        const aero = aeroField ? aeroField.surfaceAt(wx, wz, this.aeroIds) : null;
        const cls = aero ? 0 : this.classify(wx, wz, near);
        const isWater = !aero && cls >= 3;
        // Per-column ground height (runway flatten > real relief > flat 63).
        const g = aero ? aero.flatY : this.groundYAt(wx, wz);
        let top = aero ? aero.surface : cls === 1 ? ids.road : cls === 2 ? ids.sidewalk : isWater ? ids.water : ids.grass;
        // Baked imagery repaints ONLY natural ground (OVL-4): a yard/driveway/field/roof
        // takes the nearest palette colour; roads/water/aeroway keep their vector state.
        if (this.surfaceOverride && !aero && cls === 0) top = this.surfaceOverride(wx, wz, top);
        // Fill from the range floor (Atlas) or a surface-following crust floor (Earth,
        // so a deep world costs like a normal one — the void below is documented).
        const floor = this.crustDepth === null ? minY : Math.max(minY, g - this.crustDepth);
        for (let y = floor; y <= g; y++) {
          set(lx, y, lz, y === floor ? ids.bedrock : y < g - 2 ? ids.stone : y < g ? ids.dirt : top);
        }
        if (isWater) {
          // The lake fills up to its own polygon level, floored at the local ground
          // (its own level, not a global sea, ELV-3); at least its top 2 blocks.
          const level = Math.max(g, this.waterLevels[cls - 3] ?? g);
          for (let y = g; y <= level; y++) set(lx, y, lz, ids.water);
          set(lx, Math.max(minY, g - 1), lz, ids.water);
        }
      }
    }

    // Poisson trees inside the parks (ATL-4): trunk + jittered canopy, clipped to chunk.
    for (const [tx, tz] of this.trees) {
      const x = Math.round(tx), z = Math.round(tz);
      if (x < pad.minX || x > pad.maxX || z < pad.minZ || z > pad.maxZ) continue;
      this.stampTree(x, z, cx, cz, set);
    }

    // Buildings (ATL-5) — foundation-adapted to relief (ELV-4/E-27).
    const groundAt = (x: number, z: number): number => this.groundYAt(x, z);
    for (const plan of this.plans) {
      if (!bboxIntersects(plan.bbox, pad)) continue;
      stampBuilding(plan, cx, cz, plan.floorY, ids, set, groundAt);
    }

    lightChunk(chunk, this.opacityOf, this.emissionOf);
    chunk.status = 'full';
    return chunk;
  }

  /** A 5-log oak trunk + 3×3×3 leaf canopy with ±1 jitter (ATL-4), chunk-clipped. */
  private stampTree(x: number, z: number, cx: number, cz: number, set: (lx: number, y: number, lz: number, s: number) => void): void {
    const bx = cx * WORLD.SECTION, bz = cz * WORLD.SECTION;
    const inChunk = (wx: number, wz: number): boolean => wx >= bx && wx < bx + WORLD.SECTION && wz >= bz && wz < bz + WORLD.SECTION;
    const put = (wx: number, y: number, wz: number, s: number): void => { if (inChunk(wx, wz) && y >= this.range.minY && y <= this.range.maxY) set(wx - bx, y, wz - bz, s); };
    const g = this.groundYAt(x, z); // a tree grows from its own column's ground (relief)
    const rng = treeShapeRng(x, z);
    const jx = rng.nextInt(3) - 1, jz = rng.nextInt(3) - 1;
    for (let i = 1; i <= 5; i++) put(x, g + i, z, this.ids.log); // trunk
    const cy = g + 5;
    for (let dy = -1; dy <= 1; dy++) for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
      const lx = x + dx + (dy === 1 ? jx : 0), ly = cy + dy + 1, lz = z + dz + (dy === 1 ? jz : 0);
      if (dx === 0 && dz === 0 && dy < 1) continue; // leave room for the trunk top
      put(lx, ly, lz, this.ids.leaves);
    }
  }

  /** Deterministic surface height at a world column (one above the ground; real
   *  relief on M30, a constant on flat Atlas worlds). */
  terrainHeight(x = 0, z = 0): number { return this.groundYAt(x, z) + 1; }
}
